import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faLock } from '@fortawesome/free-solid-svg-icons';
import Lottie from 'lottie-react';
import confettiAnimation from '../Confetti.json';
import doneAnimation from '../Done.json';
import { Mixpanel } from '../../../config/Mixpanel';
import Constants from '../../../common/constants';


const benefits =["Unlimited Certificates" , "CV & LinkedIn Review" , "Industry Recognised Projects" , "Access To 3000+ Courses"]
const CvLoader = () => {
  const firstName = useSelector((store) => store?.profileDetails?.first_name);
  const [visibleElements, setVisibleElements] = useState({
    title: false,
    subtitle: false,
    card: false,
    button: false,
    redirectText: false,
  });
  const [countdown, setCountdown] = useState(5);
  const [showDoneAnimation, setShowDoneAnimation] = useState(false);
  const TOTAL_SECONDS = 5;

  // Get goal/role from store or use default
  const userGoal = useSelector(
    (store) => store?.onBoarding?.goal
  );

  useEffect(() => {
    // Sequential animation timing
    const timers = {
      title: 200,
      subtitle: 500,
      card: 800,
      button: 1200,
      redirectText: 1500,
    };

    Object.keys(timers).forEach((key) => {
      const timer = setTimeout(() => {
        setVisibleElements((prev) => ({ ...prev, [key]: true }));
      }, timers[key]);

      return () => clearTimeout(timer);
    });

    // Show done animation 2.5 seconds after component mounts
    const doneAnimationTimer = setTimeout(() => {
      setShowDoneAnimation(true);
    }, 2500);

    return () => clearTimeout(doneAnimationTimer);
  }, []);

  useEffect(() => {
    if (visibleElements.redirectText && countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            window.location.href = `https://resume.learntube.ai?utm_source=onboarding-flow`
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [visibleElements.redirectText, countdown]);

  useEffect(() => {
    Mixpanel.track(Constants.Mixpanel.ONBOARDING_PAGE_25_CV_PAGE_LOADER_LOADED);
    Mixpanel.track(Constants.Mixpanel.ONBOARDING_PAGE_25_LOADER_LOADED);

  }, []);

  return (
    <div className="w-full min-h-screen flex flex-col items-center justify-start py-8">
      {/* Animations Section */}
      <div className="relative w-[200px] h-[200px]">
        <Lottie
          animationData={confettiAnimation}
          loop={false}
          style={{ 
            width: 200, 
            height: 200,
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: showDoneAnimation ? 0 : 1,
            transition: 'opacity 0.5s ease-in-out'
          }}
        />
        <Lottie
          key={showDoneAnimation ? 'done-playing' : 'done-waiting'}
          animationData={doneAnimation}
          loop={false}
          autoplay={showDoneAnimation}
          style={{ 
            width: 200, 
            height: 200,
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: showDoneAnimation ? 1 : 0,
            transition: 'opacity 0.5s ease-in-out'
          }}
        />
      </div>

      {/* Title Section */}
      <div
        className={`text-center transition-all duration-700 ${
          visibleElements.title
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 -translate-y-8'
        }`}
      >
        <h1 className="text-[28px] font-bold text-[#DED996] leading-[130%]">
             {firstName}, Your CV & Profile Boost Has Begun!
        </h1>
      </div>

      {/* Subtitle Section */}
      <div
        className={`mt-2 text-center text-[14px] transition-all duration-700 ${
          visibleElements.subtitle
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 -translate-y-8'
        }`}
        style={{ transitionDelay: '100ms' }}
      >
        <p className="text-white/50">
        Let's make your CV & LinkedIn {' '}
          <span className="font-bold text-white">3x stronger</span>
        </p>
      </div>

      {/* Course Details Card */}
      <div
        className={`mt-12 w-full transition-all duration-700 ${
          visibleElements.card
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-8'
        }`}
        style={{ transitionDelay: '200ms' }}
      >
        <div
          className="rounded-2xl p-6 md:p-8 border border-[#406AFF]/5 relative flex flex-col items-center justify-center"
          style={{
            boxShadow: '0px -4px 20px rgba(0, 0, 0, 0.3)',
          }}
        >
          <img alt="" src="https://storage.googleapis.com/learntube-web-assets/files/OnBoardingPage/Ellipse%201267.png" className='absolute top-0 left-0 -mt-4 w-full h-8'/>
          <h2 className="text-[16px] font-medium text-white">
            <FontAwesomeIcon icon={faLock}/> Features You've Unlocked
          </h2>

          <div className="mt-4 flex flex-col justify-center items-center gap-4">
            {benefits?.map((benefit, index) => <div className="flex items-center justify-center gap-3 text-[14px] font-semibold text-[#70AEFF]">
              <FontAwesomeIcon
                icon={faCheck}
                className="text-[#2BBD13] mt-1 flex-shrink-0"
                size=""
              />
              <p className="">
                {benefit}
              </p>
            </div>)}
          </div>

          <p className="mt-4 text-white/50 text-[12px]">
            & Many More...
          </p>
        </div>
      </div>

      {/* Button Section */}
      <div
        className={`w-full mt-12 transition-all duration-700 ${
          visibleElements.button
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-8'
        }`}
        style={{ transitionDelay: '300ms' }}
        >
          <button
            className="relative overflow-hidden w-full h-14 md:h-16 rounded-xl bg-[#7FC241] text-white font-semibold text-[16px] text-[#121212] shadow-lg hover:bg-[#7FC241]/90 transition-all duration-300 flex items-center justify-center gap-2"
          >
            <span>Kickstart My CV & Profile Boosting Journey</span>

            {/* Animated progress line based on remaining seconds */}
            <div className="absolute w-full bottom-0 h-[4px] rounded-full bg-[#7FC241] ">
              <div
                className="h-full rounded-full bg-white transition-all duration-1000 ease-linear origin-right"
                style={{
                  transform: `scaleX(${Math.max(
                    0,
                    Math.min(1, countdown / TOTAL_SECONDS)
                  )})`,
                }}
              />
            </div>
          </button>
      </div>

      {/* Redirect Text */}
      <div
        className={`mt-4 text-center transition-all duration-700 ${
          visibleElements.redirectText
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-8'
        }`}
        style={{ transitionDelay: '400ms' }}
      >
        <p className="text-white/70 text-[12px]">
          Redirecting in{' '}
          <span className="">{countdown}</span>{' '}
          {countdown === 1 ? 'second' : 'seconds'}...
        </p>
      </div>
    </div>
  );
};

export default CvLoader;

